import { Filter } from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./styles.scss";

export interface IColumnFilterPopoverProps {
  isActive: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onRequestClose: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}

interface Position {
  top: number;
  left: number;
}

const VIEWPORT_MARGIN = 8;

export const ColumnFilterPopover: React.FunctionComponent<
  IColumnFilterPopoverProps
> = ({ isActive, isOpen, onToggle, onRequestClose, ariaLabel, children }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  // Place the dropdown just below the trigger, in viewport coordinates.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 6, left: rect.left });
  }, [isOpen]);

  // Once the dropdown has real dimensions, clamp/flip it back into the viewport.
  useLayoutEffect(() => {
    if (!isOpen || !position || !dropdownRef.current || !triggerRef.current) {
      return;
    }
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();

    let nextLeft = position.left;
    if (nextLeft + dropdownRect.width > window.innerWidth - VIEWPORT_MARGIN) {
      nextLeft = Math.max(
        VIEWPORT_MARGIN,
        window.innerWidth - dropdownRect.width - VIEWPORT_MARGIN
      );
    }

    let nextTop = position.top;
    if (nextTop + dropdownRect.height > window.innerHeight - VIEWPORT_MARGIN) {
      const flippedTop = triggerRect.top - dropdownRect.height - 6;
      nextTop =
        flippedTop >= VIEWPORT_MARGIN
          ? flippedTop
          : Math.max(
              VIEWPORT_MARGIN,
              window.innerHeight - dropdownRect.height - VIEWPORT_MARGIN
            );
    }

    if (nextTop !== position.top || nextLeft !== position.left) {
      setPosition({ top: nextTop, left: nextLeft });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, position]);

  // The dropdown is fixed-positioned in a portal, so it can't track scroll/resize
  // live without a repositioning loop; simplest is to close it instead.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    window.addEventListener("scroll", onRequestClose, true);
    window.addEventListener("resize", onRequestClose);
    return () => {
      window.removeEventListener("scroll", onRequestClose, true);
      window.removeEventListener("resize", onRequestClose);
    };
  }, [isOpen, onRequestClose]);

  return (
    <div
      className="common-column-filter-popover"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`common-column-filter-trigger ${isActive ? "active" : ""}`}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <Filter size={14} aria-hidden="true" />
      </button>
      {isOpen &&
        position &&
        createPortal(
          <div
            ref={dropdownRef}
            className="common-column-filter-dropdown"
            style={{ top: position.top, left: position.left }}
            onClick={(event) => event.stopPropagation()}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ColumnFilterPopover;
